import type { ServicePoint } from '@/services/service/serviceApi';

/** Standard: visa endast underhåll som förfaller inom detta km-intervall. */
export const DEFAULT_SERVICE_DUE_HORIZON_KM = 300;
/** Standard: visa endast underhåll vars nästa datum ligger inom detta antal dagar. */
export const DEFAULT_SERVICE_DUE_HORIZON_DAYS = 60;
/** Besiktning är enbart datum och visas så här många dagar före utgång. */
export const INSPECTION_DUE_HORIZON_DAYS = 40;

export function isInspectionServicePoint(sp: {
  serviceTypeCategory?: string | null;
  serviceTypeCode?: string | null;
  serviceTypeName?: string | null;
}): boolean {
  const category = (sp.serviceTypeCategory || '').toUpperCase();
  if (category === 'INSPECTION') return true;
  const code = (sp.serviceTypeCode || '').toUpperCase();
  if (code === 'INSPECTION' || code.includes('BESIKTNING')) return true;
  const name = (sp.serviceTypeName || '').toLowerCase();
  return name.includes('besiktning') || name === 'inspection';
}

function parseDateValue(rawValue: string | null | undefined): Date | null {
  const raw = (rawValue || '').trim().replace(/^~\s*/, '');
  if (!raw) return null;
  const iso = raw.includes('T') ? raw : `${raw}T12:00:00`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseDueDate(sp: ServicePoint, dateOnly: boolean): Date | null {
  return parseDateValue(dateOnly ? sp.nextDueDate : (sp.estimatedDueDate ?? sp.nextDueDate));
}

function isDateDueOnOrBefore(due: Date, maxDays: number, includeOverdue: boolean): boolean {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const limit = new Date(start);
  limit.setDate(limit.getDate() + maxDays);
  const d = new Date(due);
  d.setHours(0, 0, 0, 0);
  if (includeOverdue && d < start) return true;
  return d >= start && d <= limit;
}

/**
 * Sant om servicen snart förfaller enligt km **eller** kalenderdatum (inkl. samma dag som "idag").
 * Besiktning: enbart officiellt datum, 40 dagar före utgång eller redan förfallen.
 */
export function isServicePointDueWithinHorizon(
  sp: ServicePoint,
  options: { maxKm?: number; maxDays?: number } = {}
): boolean {
  if (isInspectionServicePoint(sp)) {
    if (sp.status === 'BOOKED') return true;
    const due = parseDueDate(sp, true);
    if (!due) return false;
    return isDateDueOnOrBefore(due, INSPECTION_DUE_HORIZON_DAYS, true);
  }

  const maxKm = options.maxKm ?? DEFAULT_SERVICE_DUE_HORIZON_KM;
  const maxDays = options.maxDays ?? DEFAULT_SERVICE_DUE_HORIZON_DAYS;

  if (sp.nextDueKm != null && sp.currentKm != null) {
    const kmLeft = sp.nextDueKm - sp.currentKm;
    if (kmLeft >= 0 && kmLeft <= maxKm) return true;
  }

  const due = parseDueDate(sp, false);
  if (due) {
    return isDateDueOnOrBefore(due, maxDays, false);
  }

  return false;
}
